import { Navigation } from '@root/sanity/sanity.types';
import useVisible from '@root/src/hooks/useVisible';
import useLockBodyScroll from '@root/src/hooks/useLockBodyScroll';
import Logo from '@root/src/components/Logo';
import LogoIcon from '@root/src/components/LogoIcon';
import IconX from '@root/src/components/IconX';
import IconMenu from '@root/src/components/IconMenu';
import TrackedLink from 'components/sanity/TrackedLink';
import { NavTrackingProvider, useNavTracking } from './analytics';
import HelpButton from './HelpButton';
import NavItem, { navItemClassNames, desktopNavItemClassNames } from './NavItem';
import { builtInLinks, navItemLink } from './navEvents';

type NavMainProps = {
  pathname: string;
  navigation: Navigation;
};

type MobileMenuButtonProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
};

const MobileMenuButton = ({ isVisible, setIsVisible }: MobileMenuButtonProps) => {
  const { trackOpen } = useNavTracking();

  const toggleMenu = () => {
    if (!isVisible) {
      trackOpen();
    }

    setIsVisible(!isVisible);
  };

  return (
    <div onClick={toggleMenu} className="flex cursor-pointer flex-row items-center px-20 py-16 lg:hidden">
      {isVisible ? (
        <IconX className="h-20 w-20" style={{ color: '#878787' }} />
      ) : (
        <IconMenu className="h-20 w-20" style={{ color: '#878787' }} />
      )}
    </div>
  );
};

const NavMain = ({ pathname, navigation }: NavMainProps) => {
  const { ref, isVisible, setIsVisible } = useVisible(false);

  useLockBodyScroll(isVisible);

  const renderNavItems = (isMobile: boolean) =>
    navigation.items
      ?.filter((item) => !item.hidden && !item?.image?.asset)
      .map((item) => (
        <NavTrackingProvider item={navItemLink(item, navigation.items)} key={`${item.title}-${item.href}`}>
          <NavItem isMobile={isMobile} item={item} pathname={pathname} setIsVisible={setIsVisible} />
        </NavTrackingProvider>
      ));

  return (
    <>
      <header id="header">
        <nav className="flex items-stretch justify-between pl-16 md:pl-32 lg:space-x-32">
          <TrackedLink
            nav
            navItem={builtInLinks.logo}
            href="/"
            className="tracker-nav-main_nav-logo-20200108-150221 flex items-center py-8"
          >
            <Logo className="hidden hover:text-gold-dark xs:block" />
            <LogoIcon className="xs:hidden" />
          </TrackedLink>

          <div ref={ref} className="nav-main-primary relative z-50 flex flex-grow items-stretch">
            <NavTrackingProvider surface="mobile_menu">
              <NavTrackingProvider item={builtInLinks.mobileMenu}>
                <MobileMenuButton isVisible={isVisible} setIsVisible={setIsVisible} />
              </NavTrackingProvider>

              {/*Mobile NavBar*/}
              <div
                style={{
                  top: '105px',
                }}
                className={`${isVisible ? '' : 'hidden'}
               fixed left-0 bottom-0 overflow-auto bg-white px-20 py-2 shadow-2 lg:hidden`}
              >
                {renderNavItems(true)}
              </div>
            </NavTrackingProvider>

            {/*Desktop NavBar*/}
            <div
              className={`absolute top-56 hidden space-y-8 bg-white p-32 shadow-2 lg:relative lg:top-auto lg:flex lg:flex-grow lg:flex-row lg:items-center lg:space-y-0 lg:space-x-32 lg:p-0 lg:shadow-none`}
            >
              {renderNavItems(false)}
            </div>

            <div className="hidden items-stretch xs:flex lg:hidden">
              <TrackedLink
                nav
                navItem={builtInLinks.smallScreenCollection}
                as="/collection/tuxedos-and-suits"
                href="/collection/[category]"
                className={`${navItemClassNames} ${desktopNavItemClassNames} tracker-nav-main_nav-small_screen_collection-20200108-150221 ${
                  pathname.includes('/collection/') ? 'text-gold-darker' : 'text-gray-darker'
                }`}
              >
                Collection
              </TrackedLink>
            </div>
          </div>

          <div className="flex items-stretch space-x-16 lg:space-x-32 xl:pr-0 lg-only:pr-32">
            <TrackedLink
              nav
              reloadDocument
              navItem={builtInLinks.login}
              className={`text-h5 ${navItemClassNames} ${desktopNavItemClassNames}`}
              href={`${process.env.NEXT_PUBLIC_APP_URL}/account/login`}
            >
              Login
            </TrackedLink>

            <TrackedLink
              nav
              reloadDocument
              navItem={builtInLinks.startYourEvent}
              className={`text-h5 flex items-center ${navItemClassNames} ${desktopNavItemClassNames}`}
              href={`${process.env.NEXT_PUBLIC_APP_URL}/signup/email`}
            >
              <div className="btn-info btn">Start Your Event</div>
            </TrackedLink>
          </div>

          <HelpButton trackerClass={'tracker-nav-main_nav-help-210730-130721'} />
        </nav>
      </header>
    </>
  );
};

export default NavMain;
